import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import BookWithoutAccountCTA from "@/components/BookWithoutAccountCTA";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.prefill_email || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Phase 2D-fix — Capture `?company=<slug>` and persist it in
  // sessionStorage so that AuthContext.logout() can later redirect the
  // user back to /:slug even when they arrived here directly (without
  // passing through CompanyRedirect / CompanyLanding). Mirrors the
  // exact same key + validation used by those two components.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const slug = (params.get("company") || "").trim();
      if (slug && /^[a-z0-9][a-z0-9\-]{0,62}$/i.test(slug)) {
        sessionStorage.setItem("sdrive_tenant_slug", slug);
      }
    } catch { /* sessionStorage unavailable — non-critical */ }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join("; ") : t("login.invalid"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/52e0b8dc-c060-4dc7-8712-962da5ad03fa/images/0d3764806606caca2d24fe0a5eb45c5247a9ddbb581b22b9b425989daccd5891.png)` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
      <div className="relative z-10 flex flex-col flex-1">
        <PublicHeader langTestId="login-lang" />
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-sm animate-fade-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4"><Car size={28} weight="fill" className="text-amber-500" /></div>
              <h1 data-testid="login-title" className="text-3xl font-light tracking-tight text-white">S.DRIVE</h1>
              <p className="text-sm text-zinc-500 tracking-widest uppercase mt-1">{t("login.subtitle")}</p>
            </div>
            <form onSubmit={handleSubmit} className="bg-[#121212] border border-zinc-800 rounded-lg p-6 space-y-5">
              {error && <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded px-3 py-2">{error}</div>}
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("login.email")}</Label>
                <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" required className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 focus:ring-amber-500/20 h-11" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("login.password")}</Label>
                  <button
                    data-testid="login-forgot-link"
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-xs text-amber-500 hover:text-amber-400 transition-colors"
                  >
                    {t("login.forgot")}
                  </button>
                </div>
                <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("login.password_ph")} required className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 focus:ring-amber-500/20 h-11" />
              </div>
              <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 active:scale-[0.98] transition-all h-11">
                {loading ? t("login.submitting") : t("login.submit")}
              </Button>
              <p className="text-center text-sm text-zinc-500">{t("login.driver_q")} <button type="button" onClick={() => navigate("/register")} className="text-amber-500 hover:text-amber-400">{t("login.create_acc")}</button></p>
              <p className="text-center text-sm text-zinc-500">{t("login.agency_q")} <button data-testid="agency-register-link" type="button" onClick={() => navigate("/register-agency")} className="text-amber-500 hover:text-amber-400">{t("login.create_acc")}</button></p>
              <p className="text-center text-xs text-zinc-600 pt-1">
                Client privé ?{" "}
                <button data-testid="client-register-link" type="button" onClick={() => navigate("/register-client")} className="text-amber-500 hover:text-amber-400">Créer mon espace</button>
                {" · "}
                <button data-testid="quote-recovery-link" type="button" onClick={() => navigate("/quote-recovery")} className="text-amber-500 hover:text-amber-400">Retrouver une demande</button>
              </p>
            </form>
            <BookWithoutAccountCTA testid="login-book-no-account" />
          </div>
        </div>
        <PublicFooter />
      </div>
    </div>
  );
}
